<template>
  <div class="lazy-image-wrapper" :class="wrapperClass">
    <img
      ref="lazyImg"
      :class="[
        'lazy-image',
        {
          'lazy-loading': isLoading,
          'lazy-loaded': isLoaded,
          'lazy-error': hasError
        }
      ]"
      :src="placeholderSrc"
      :alt="alt"
      :data-lazy-src="optimizedSrc"
      :data-lazy-type="'image'"
      @load="onLoad"
      @error="onError"
    />
    
    <!-- 加载状态 -->
    <div v-if="showPlaceholder && (isLoading || (!isLoaded && !hasError))" class="lazy-placeholder">
      <div class="lazy-spinner" v-if="!hasError">
        <div class="spinner-ring"></div>
      </div>
      <div class="lazy-error-msg" v-else>
        <i class="el-icon-picture-outline"></i>
        <span>图片加载失败</span>
      </div>
    </div>
  </div>
</template>

<script>
import lazyLoader, { imageCompressor } from '@/utils/lazyLoad'

export default {
  name: 'LazyImage',
  props: {
    // 图片源地址
    src: {
      type: String,
      required: true
    },
    // 占位图
    placeholder: {
      type: String,
      default: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI2NjYyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWKoOi9veS4rS4uLjwvdGV4dD48L3N2Zz4='
    },
    // 图片描述
    alt: {
      type: String,
      default: ''
    },
    // 是否显示占位符
    showPlaceholder: {
      type: Boolean,
      default: true
    },
    // 包装器样式类
    wrapperClass: {
      type: String,
      default: ''
    },
    // 是否启用响应式图片
    responsive: {
      type: Boolean,
      default: true
    },
    // 响应式尺寸
    sizes: {
      type: Array,
      default: () => [480, 768, 1024, 1920]
    }
  },
  
  data() {
    return {
      isLoading: false,
      isLoaded: false,
      hasError: false
    }
  },
  
  computed: {
    placeholderSrc() {
      return this.isLoaded ? this.optimizedSrc : this.placeholder
    },
    
    optimizedSrc() {
      if (this.responsive) {
        return imageCompressor.getOptimalImageUrl(this.src, this.sizes)
      }
      return this.src
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.lazyImg) {
        lazyLoader.observe(this.$refs.lazyImg)
      }
    })
  },
  
  beforeDestroy() {
    if (this.$refs.lazyImg && lazyLoader.observer) {
      lazyLoader.observer.unobserve(this.$refs.lazyImg)
    }
  },
  
  methods: {
    onLoad() {
      this.isLoaded = true
      this.isLoading = false
      this.hasError = false
      this.$emit('load')
    },
    
    onError() {
      this.hasError = true
      this.isLoading = false
      this.isLoaded = false
      this.$emit('error')
    },
    
    // 手动重新加载
    reload() {
      this.hasError = false
      this.isLoaded = false
      this.isLoading = true
      
      if (this.$refs.lazyImg) {
        lazyLoader.observe(this.$refs.lazyImg)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.lazy-image-wrapper {
  position: relative;
  display: inline-block;
  overflow: hidden;
  
  .lazy-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.3s ease;
    
    &.lazy-loading {
      opacity: 0.6;
    }
    
    &.lazy-loaded {
      opacity: 1;
    }
    
    &.lazy-error {
      opacity: 0.3;
    }
  }
  
  .lazy-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    
    .lazy-spinner {
      .spinner-ring {
        width: 24px;
        height: 24px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #409eff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
    
    .lazy-error-msg {
      display: flex;
      flex-direction: column;
      align-items: center;
      color: #999;
      font-size: 12px;
      
      i {
        font-size: 24px;
        margin-bottom: 4px;
      }
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 响应式样式
@media (max-width: 768px) {
  .lazy-image-wrapper {
    .lazy-placeholder {
      .lazy-spinner .spinner-ring {
        width: 20px;
        height: 20px;
      }
      
      .lazy-error-msg {
        font-size: 10px;
        
        i {
          font-size: 20px;
        }
      }
    }
  }
}
</style> 